import { useCallback, useEffect, useState } from 'react';
import { Flex, Spinner } from '@banhala/ui-kit';
import { WebView } from '../WebWebView';
import { isWeb } from '../../utils';
import { useSafeTimeout } from '../../hooks/useSafeTimeout';

/**
 * 첫 로드가 끝났다는 신호(`onLoad`)가 오지 않을 때의 스피너 상한. 프레임이 차단되거나
 * 로드 이벤트가 유실돼도 스피너가 영구히 남지 않게 한다.
 */
const LOADING_TIMEOUT_MS = 8_000;

type LoadingWebViewProps = {
  /** 로드할 URL. */
  uri: string;
  onMessage?: (params: { nativeEvent: { data?: string } }) => void;
};

/**
 * 첫 로드가 끝날 때까지 스피너를 덮는 웹뷰.
 *
 * 웹뷰는 로드 전에 아무것도 그리지 않아 흰 화면만 보인다(특히 웹의 iframe). 진입 직후
 * 빈 화면이 어색하므로 로드 완료(`onLoad`)까지 스피너로 덮는다.
 *
 * 로딩 상태는 URL 마다 새로 시작해야 한다. 호출부가 `key={uri}` 로 리마운트하거나, URL 이
 * 바뀌지 않는 화면이라면 그대로 두면 된다.
 *
 * ⚠️ 부모는 이 컴포넌트가 채울 영역(`flex: 1` 등)을 가진 컨테이너여야 한다. 스피너를
 * absolute 로 겹치므로 부모 박스 기준으로 덮인다.
 */
export const LoadingWebView = ({ uri, onMessage }: LoadingWebViewProps) => {
  // 네이티브 웹뷰(`RNAblyWebView`)는 로드 완료 이벤트를 올려주지 않아 스피너를 내릴 수
  // 없다. 그래서 로딩 표시는 웹에서만 한다.
  const [isLoading, setIsLoading] = useState(isWeb);
  const stopLoading = useCallback(() => setIsLoading(false), []);
  const setSafeTimeout = useSafeTimeout();

  useEffect(() => {
    if (!isLoading) return;
    setSafeTimeout(stopLoading, LOADING_TIMEOUT_MS);
  }, [isLoading, stopLoading, setSafeTimeout]);

  return (
    <>
      <WebView
        source={{ uri }}
        onMessage={onMessage}
        onLoad={stopLoading}
      />
      {isLoading && (
        <Flex.Center
          position={'absolute'}
          top={0}
          bottom={0}
          left={0}
          right={0}
          backgroundColor={'white'}
        >
          <Spinner />
        </Flex.Center>
      )}
    </>
  );
};
